import React from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography, useMediaQuery } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import { FONT, R, labelSx, outlineButtonSx, thinScrollSx } from "./roomTheme";

/*
 * Dialogs for lot information. Display only — the pages keep their own open / close state,
 * zoom state and data.
 */

function DialogHeader({ children, onClose }) {
  return (
    <DialogTitle
      component="div"
      sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, px: { xs: 2.5, sm: 3 }, py: 2 }}
    >
      <Box sx={{ minWidth: 0 }}>{children}</Box>
      <IconButton onClick={onClose} aria-label="Close" sx={{ borderRadius: 0, mt: -0.5, mr: -1 }}>
        <CloseIcon />
      </IconButton>
    </DialogTitle>
  );
}

/**
 * Complete details of the current lot: lot number, artist, title and every catalogue field
 * the room already loads (HTML from the catalogue). Closes with X, Close, Escape or a click
 * outside. Opening / closing only toggles the dialog — nothing else in the room changes.
 * The content scrolls inside the dialog; full screen on phones.
 */
export function LotDetailsDialog({ open, onClose, items, lot }) {
  const fullScreen = useMediaQuery("(max-width:600px)");
  const hasArtist = lot && !(lot.artist === null || lot.artist === "" || lot.artist === undefined);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen} scroll="paper" aria-labelledby="lot-details-title" PaperProps={{ sx: { borderRadius: 0 } }}>
      <DialogHeader onClose={onClose}>
        <Typography id="lot-details-title" sx={{ ...labelSx, color: R.ink, letterSpacing: "3px", fontSize: "0.8rem" }}>
          {lot ? `Lot ${lot.lot_number}` : "Lot Details"}
        </Typography>
        {lot && (
          <>
            <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: { xs: "1.2rem", sm: "1.35rem" }, textTransform: "uppercase", lineHeight: 1.25, mt: 0.5, color: R.ink }}>
              {hasArtist ? lot.artist : lot.title}
            </Typography>
            {hasArtist && (
              <Typography component="div" sx={{ fontFamily: FONT, fontStyle: "italic", fontSize: { xs: "1.05rem", sm: "1.1rem" }, lineHeight: 1.35, mt: 0.25, color: R.text }} dangerouslySetInnerHTML={{ __html: lot.title }} />
            )}
          </>
        )}
      </DialogHeader>
      <DialogContent dividers sx={{ px: { xs: 2.5, sm: 3 }, py: 2.5, ...thinScrollSx }}>
        {items.map(
          (item, index) =>
            item.val?.trim() && (
              <Box key={index} sx={{ mb: 3 }}>
                <Typography sx={labelSx}>{item.label}</Typography>
                <Typography
                  component="div"
                  dangerouslySetInnerHTML={{ __html: item.val }}
                  sx={{ fontFamily: FONT, fontSize: "1rem", color: R.text, lineHeight: 1.55, mt: 0.5, whiteSpace: "pre-line" }}
                />
              </Box>
            )
        )}
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2.5, sm: 3 }, py: 1.5 }}>
        <Button onClick={onClose} fullWidth={fullScreen} sx={outlineButtonSx}>
          Close Details
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Larger view of a lot from the lot list. With `zoom` (desktop list) it offers zoom / pan
 * using the page's own zoom state; without it, a plain contained image.
 */
export function LotPreviewDialog({ open, onClose, image, lotNumber, withdrawn, artist, title, soldPrice, soldColor, zoom }) {
  const fullScreen = useMediaQuery("(max-width:600px)");
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={zoom ? "md" : "sm"} fullScreen={fullScreen} aria-labelledby="lot-preview-title" PaperProps={{ sx: { borderRadius: 0 } }}>
      <DialogHeader onClose={onClose}>
        {withdrawn === 1 ? (
          <Typography id="lot-preview-title" component="span" sx={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>
            Lot preview
          </Typography>
        ) : (
          <>
            <Typography id="lot-preview-title" sx={{ ...labelSx, color: R.ink, letterSpacing: "3px" }}>
              Lot {lotNumber}
            </Typography>
            <Typography component="div" sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "1.2rem", textTransform: "uppercase", mt: 0.5 }} dangerouslySetInnerHTML={{ __html: artist }} />
            <Typography component="div" sx={{ fontFamily: FONT, fontStyle: "italic", fontSize: "1.05rem", mt: 0.25 }} dangerouslySetInnerHTML={{ __html: title }} />
            {soldPrice && <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "1.1rem", color: soldColor, mt: 0.5 }}>{soldPrice}</Typography>}
          </>
        )}
      </DialogHeader>

      <DialogContent dividers sx={{ p: 0, bgcolor: R.soft, position: "relative", height: fullScreen ? "auto" : zoom ? 440 : "auto", flex: fullScreen ? 1 : undefined, overflow: "hidden" }}>
        {zoom ? (
          <Box
            onMouseDown={zoom.onMouseDown}
            onMouseMove={zoom.onMouseMove}
            onMouseUp={zoom.onMouseUp}
            onMouseLeave={zoom.onMouseUp}
            role="img"
            aria-label={`Lot ${lotNumber}`}
            sx={{
              width: "100%",
              height: "100%",
              minHeight: 320,
              cursor: zoom.level > 1 ? "grab" : "default",
              backgroundImage: `url(${image})`,
              backgroundRepeat: "no-repeat",
              backgroundColor: R.soft,
              backgroundPosition: zoom.level === 1 ? "center center" : `${zoom.offset.x}px ${zoom.offset.y}px`,
              backgroundSize: zoom.level === 1 ? "contain" : `${zoom.level * 100}%`,
              transition: zoom.dragging ? "none" : "background-size 0.25s ease-out",
              padding: "20px",
              backgroundOrigin: "content-box",
              backgroundClip: "content-box",
              boxSizing: "border-box",
            }}
          />
        ) : (
          <Box component="img" src={image} alt={`Lot ${lotNumber}`} sx={{ display: "block", width: "100%", height: fullScreen ? "100%" : "auto", maxHeight: fullScreen ? "70vh" : 420, objectFit: "contain", p: 2, boxSizing: "border-box" }} />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, justifyContent: zoom ? "space-between" : "flex-end" }}>
        {zoom && (
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton onClick={zoom.onZoomOut} aria-label="Zoom out" sx={{ border: `1px solid ${R.lineStrong}`, borderRadius: 0 }}>
              <ZoomOutIcon />
            </IconButton>
            <IconButton onClick={zoom.onZoomIn} aria-label="Zoom in" sx={{ border: `1px solid ${R.lineStrong}`, borderRadius: 0 }}>
              <ZoomInIcon />
            </IconButton>
          </Box>
        )}
        <Button onClick={onClose} sx={outlineButtonSx} fullWidth={fullScreen && !zoom}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
